import { Link, useNavigate } from "@tanstack/react-router";
import { BellIcon, CoinsIcon, LinkIcon, UserRoundIcon } from "lucide-react";
import type { ReactNode } from "react";
import logo from "@/assets/images/logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip.tsx";
import { MISSING, showValue } from "@/lib/display.ts";
import { cn } from "@/lib/utils.ts";
import { HELP_NAV, type NavItem, PRIMARY_NAV } from "./nav.ts";

export interface HeaderData {
  unreadCount?: number | null;
  credits?: string | number | null;
  accountName?: string | null;
  userId?: string | null;
}

const HEADER_TOOL =
  "relative flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground";

function MessagesButton({ unreadCount }: { unreadCount: number | null | undefined }) {
  const known = typeof unreadCount === "number";
  const label = !known
    ? "系统消息，未读数未知"
    : unreadCount > 0
      ? `系统消息，${unreadCount} 条未读`
      : "系统消息，没有未读";
  return (
    <Tooltip>
      <TooltipTrigger render={<Link to="/messages" aria-label={label} className={HEADER_TOOL} />}>
        <BellIcon aria-hidden className="size-[18px]" />
        {known && unreadCount > 0 ? (
          <span
            aria-hidden
            data-testid="unread-badge"
            className="bg-destructive absolute -top-0.5 right-0 min-w-4 rounded-full px-1 text-center text-[10px] leading-4 text-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
      </TooltipTrigger>
      <TooltipContent>系统消息</TooltipContent>
    </Tooltip>
  );
}

function AccountMenu({
  accountName,
  userId,
}: {
  accountName: string | null | undefined;
  userId: string | null | undefined;
}) {
  const navigate = useNavigate();
  const name = accountName || userId || undefined;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={name ? `账号菜单：${name}` : "账号菜单"}
        className="hover:bg-muted flex size-8 items-center justify-center rounded-md"
      >
        <span className="bg-primary text-primary-foreground flex size-7 items-center justify-center rounded-md text-xs font-medium">
          {name ? name.slice(0, 1).toUpperCase() : <UserRoundIcon aria-hidden className="size-4" />}
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs font-normal">当前账号</span>
            <span className="text-foreground truncate">{name ?? MISSING}</span>
          </DropdownMenuLabel>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void navigate({ to: "/account" })}>
          账号管理
        </DropdownMenuItem>
        <DropdownMenuItem disabled>退出登录</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AppHeader({ unreadCount, credits, accountName, userId }: HeaderData) {
  return (
    <header className="bg-background col-span-2 flex h-12 items-center justify-between gap-3 border-b px-3">
      <Link to="/" aria-label="量枢首页" className="flex h-full items-center">
        <img src={logo} alt="中科量枢" className="h-7 w-auto" />
      </Link>
      <div className="flex items-center gap-1.5">
        <MessagesButton unreadCount={unreadCount} />
        <Link to="/credits" aria-label="算力点数" className={cn(HEADER_TOOL, "bg-muted/60")}>
          <CoinsIcon aria-hidden className="size-4" />
          <span data-testid="header-credits" className="tabular-nums">
            {showValue(credits)}
          </span>
          <span className="text-xs">点</span>
        </Link>
        <AccountMenu accountName={accountName} userId={userId} />
      </div>
    </header>
  );
}

function NavLink({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      className="group text-muted-foreground hover:bg-muted hover:text-foreground aria-[current=page]:bg-accent aria-[current=page]:text-accent-foreground flex min-h-16 flex-col items-center justify-center gap-1 rounded-md px-0.5 text-[11px] whitespace-nowrap aria-[current=page]:font-medium"
    >
      <span className="group-aria-[current=page]:bg-primary group-aria-[current=page]:text-primary-foreground flex size-8 items-center justify-center rounded-md">
        <Icon aria-hidden className="size-[18px]" />
      </span>
      {item.label}
    </Link>
  );
}

const RAIL_TOOL =
  "flex h-10 w-full items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground";

function RailTools() {
  const HelpIcon = HELP_NAV.icon;
  return (
    <nav aria-label="关联网站与帮助" className="flex flex-col gap-1 px-1.5 py-3">
      <Popover>
        <Tooltip>
          <TooltipTrigger
            render={
              <PopoverTrigger
                render={<button type="button" aria-label="关联网站" className={RAIL_TOOL} />}
              />
            }
          >
            <LinkIcon aria-hidden className="size-[18px]" />
          </TooltipTrigger>
          <TooltipContent side="right">关联网站</TooltipContent>
        </Tooltip>
        <PopoverContent side="right" align="end">
          <p className="text-muted-foreground text-sm">即将上线</p>
        </PopoverContent>
      </Popover>
      <Tooltip>
        <TooltipTrigger
          render={<Link to={HELP_NAV.to} aria-label={HELP_NAV.label} className={RAIL_TOOL} />}
        >
          <HelpIcon aria-hidden className="size-[18px]" />
        </TooltipTrigger>
        <TooltipContent side="right">{HELP_NAV.label}</TooltipContent>
      </Tooltip>
    </nav>
  );
}

export function AppShell({ header, children }: { header?: HeaderData; children: ReactNode }) {
  return (
    <TooltipProvider>
      <div className="bg-background text-foreground grid h-dvh grid-cols-[4rem_minmax(0,1fr)] grid-rows-[auto_minmax(0,1fr)]">
        <AppHeader {...header} />
        <aside className="flex min-h-0 flex-col border-r">
          <nav
            aria-label="主导航"
            className="flex flex-1 flex-col gap-1 overflow-y-auto px-1.5 py-2"
          >
            {PRIMARY_NAV.map((item) => (
              <NavLink key={item.to} item={item} />
            ))}
          </nav>
          <RailTools />
        </aside>
        <main className="min-w-0 overflow-auto px-6 py-6">{children}</main>
      </div>
    </TooltipProvider>
  );
}
